import type { MenuAvailabilityReason, MenuAvailabilityState, MenuSpiceLevel } from '@/types/menuAvailability';

const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const dateTimeFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const formatTimestampForDisplay = (timestamp: number, now: number): string => {
  if (!Number.isFinite(timestamp)) {
    return '';
  }

  const target = new Date(timestamp);
  const current = new Date(now);
  const sameDay =
    target.getFullYear() === current.getFullYear() &&
    target.getMonth() === current.getMonth() &&
    target.getDate() === current.getDate();

  return sameDay ? timeFormatter.format(target) : dateTimeFormatter.format(target);
};

const formatCountdown = (timestamp: number, now: number): string | null => {
  const deltaMs = timestamp - now;
  if (!Number.isFinite(deltaMs) || deltaMs <= 0) {
    return null;
  }

  const totalSeconds = Math.round(deltaMs / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const totalMinutes = Math.round(totalSeconds / 60);
  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;
  if (totalHours < 48) {
    return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }

  const totalDays = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;
  return remainingHours > 0 ? `${totalDays}d ${remainingHours}h` : `${totalDays}d`;
};

const formatWindowLabel = (window: MenuAvailabilityState['currentWindow'], now: number): string | null => {
  if (!window) {
    return null;
  }

  const { start, end } = window;
  if (typeof start === 'number' && typeof end === 'number') {
    return `Window ${formatTimestampForDisplay(start, now)} – ${formatTimestampForDisplay(end, now)}`;
  }

  if (typeof end === 'number') {
    return `Window ends ${formatTimestampForDisplay(end, now)}`;
  }

  if (typeof start === 'number') {
    return `Window starts ${formatTimestampForDisplay(start, now)}`;
  }

  return null;
};

export type AvailabilityTone = 'positive' | 'warning' | 'negative' | 'info' | 'muted';

export interface AvailabilityDescriptor {
  isAvailable: boolean;
  status: MenuAvailabilityState['status'];
  statusLabel: string;
  tone: AvailabilityTone;
  primaryMessage: string;
  secondaryMessage?: string;
  countdownLabel?: string | null;
  nextChangeTimestamp?: number | null;
  currentWindowLabel?: string | null;
  reasons: MenuAvailabilityReason[];
}

const STATUS_LABELS: Record<MenuAvailabilityState['status'], string> = {
  available: 'Available',
  temporarily_unavailable: 'Temporarily unavailable',
  scheduled: 'Scheduled',
  inactive: 'Unavailable',
  expired: 'Expired',
};

const STATUS_TONES: Record<MenuAvailabilityState['status'], AvailabilityTone> = {
  available: 'positive',
  temporarily_unavailable: 'warning',
  scheduled: 'info',
  inactive: 'muted',
  expired: 'negative',
};

const resolveNextChangeTimestamp = (state: MenuAvailabilityState): number | null => {
  const candidates: number[] = [];
  if (state.soldOutUntil) {
    candidates.push(state.soldOutUntil);
  }
  if (state.nextAvailableAt) {
    candidates.push(state.nextAvailableAt);
  }
  if (state.autoExpireAt) {
    candidates.push(state.autoExpireAt);
  }
  if (state.categoryAutoExpireAt) {
    candidates.push(state.categoryAutoExpireAt);
  }
  if (state.currentWindow?.end) {
    candidates.push(state.currentWindow.end);
  }

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((earliest, entry) => {
    if (!Number.isFinite(entry)) {
      return earliest;
    }
    if (earliest === null) {
      return entry;
    }
    return entry < earliest ? entry : earliest;
  }, null as number | null);
};

export const describeAvailability = (
  state: MenuAvailabilityState,
  now: number = Date.now(),
): AvailabilityDescriptor => {
  const statusLabel = STATUS_LABELS[state.status] ?? 'Status undefined';
  const tone = STATUS_TONES[state.status] ?? 'muted';
  const nextChangeTimestamp = resolveNextChangeTimestamp(state);
  const countdownLabel = nextChangeTimestamp ? formatCountdown(nextChangeTimestamp, now) : null;

  let primaryMessage = statusLabel;
  let secondaryMessage: string | undefined;

  if (state.status === 'available') {
    const expiryTarget =
      state.autoExpireAt && state.autoExpireAt > now
        ? state.autoExpireAt
        : state.categoryAutoExpireAt && state.categoryAutoExpireAt > now
          ? state.categoryAutoExpireAt
          : state.currentWindow?.end && state.currentWindow.end > now
            ? state.currentWindow.end
            : null;

    if (expiryTarget) {
      const formatted = formatTimestampForDisplay(expiryTarget, now);
      primaryMessage = `Available until ${formatted}`;
    } else {
      primaryMessage = 'Available for ordering';
    }
  } else if (state.status === 'temporarily_unavailable') {
    if (state.soldOutUntil && state.soldOutUntil > now) {
      const formatted = formatTimestampForDisplay(state.soldOutUntil, now);
      primaryMessage = `Sold out until ${formatted}`;
    } else if (state.nextAvailableAt && state.nextAvailableAt > now) {
      const formatted = formatTimestampForDisplay(state.nextAvailableAt, now);
      primaryMessage = `Back at ${formatted}`;
    } else {
      primaryMessage = 'Temporarily unavailable';
    }
  } else if (state.status === 'scheduled') {
    if (state.nextAvailableAt && state.nextAvailableAt > now) {
      const formatted = formatTimestampForDisplay(state.nextAvailableAt, now);
      primaryMessage = `Scheduled for ${formatted}`;
    } else if (state.currentWindow?.start && state.currentWindow.start > now) {
      const formatted = formatTimestampForDisplay(state.currentWindow.start, now);
      primaryMessage = `Opens at ${formatted}`;
    } else {
      primaryMessage = 'Schedule inactive';
    }
  } else if (state.status === 'expired') {
    const expiredReason = state.reasons.find((reason) => reason.code === 'auto_expired');
    if (expiredReason && state.autoExpireAt) {
      const formatted = formatTimestampForDisplay(state.autoExpireAt, now);
      primaryMessage = `Auto-expired at ${formatted}`;
    } else {
      primaryMessage = 'Expired';
    }
  } else if (state.status === 'inactive') {
    primaryMessage = 'Item hidden from guests';
  }

  if (state.currentWindow && !secondaryMessage) {
    secondaryMessage = formatWindowLabel(state.currentWindow, now) ?? undefined;
  }

  if (!secondaryMessage && state.upcomingWindows.length > 0) {
    const nextWindow = state.upcomingWindows.find((window) => (window.start ?? 0) > now);
    if (nextWindow?.start) {
      const formatted = formatTimestampForDisplay(nextWindow.start, now);
      secondaryMessage = `Next window ${formatted}`;
    }
  }

  return {
    isAvailable: state.isAvailable,
    status: state.status,
    statusLabel,
    tone,
    primaryMessage,
    secondaryMessage,
    countdownLabel,
    nextChangeTimestamp,
    currentWindowLabel: formatWindowLabel(state.currentWindow, now),
    reasons: state.reasons,
  };
};

export interface SpiceDescriptor {
  level: MenuSpiceLevel;
  label: string;
  heat: 0 | 1 | 2 | 3 | 4;
}

const SPICE_DESCRIPTORS: Record<MenuSpiceLevel, SpiceDescriptor> = {
  none: { level: 'none', label: 'No spice', heat: 0 },
  mild: { level: 'mild', label: 'Mild', heat: 1 },
  medium: { level: 'medium', label: 'Medium', heat: 2 },
  hot: { level: 'hot', label: 'Hot', heat: 3 },
  extra_hot: { level: 'extra_hot', label: 'Extra hot', heat: 4 },
};

export const describeSpiceLevel = (level: MenuSpiceLevel | null | undefined): SpiceDescriptor | null => {
  if (!level) {
    return null;
  }
  return SPICE_DESCRIPTORS[level] ?? null;
};

export const availabilityUtils = {
  describeAvailability,
  describeSpiceLevel,
};
